import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  resource,
  Signal,
  signal,
} from '@angular/core';
import { AlertFlashService, UiBtnDirective } from '@callrail/looky/ui';
import {
  debounce,
  form,
  FormField,
  minLength,
  required,
  validate,
  validateAsync,
} from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { finalize, map } from 'rxjs';
import {
  passwordFeedback,
  StytchService,
} from '../../services/stytch/stytch.service';
import { ParamMap, ActivatedRoute } from '@angular/router';
import { PostLoginService } from '../../services/post-login/post-login.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'anbs-set-password',
  imports: [UiBtnDirective, FormField, FormsModule],
  templateUrl: './set-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetPasswordComponent implements OnInit {
  private stytch = inject(StytchService);
  private alertFlash = inject(AlertFlashService);
  private route = inject(ActivatedRoute);
  private postLogin = inject(PostLoginService);

  passwordResetToken = signal('');
  showPassword = signal(false);
  submitting = signal(false);
  passwordReset = signal({
    password: '',
    passwordConfirmation: '',
  });
  passwordResetForm = form(this.passwordReset, (schema) => {
    debounce(schema.password, 300);
    required(schema.password, { message: 'Password is required' });
    minLength(schema.password, 6, {
      message: 'Password must be at least 6 characters',
    });
    validate(schema.passwordConfirmation, ({ value, valueOf }) =>
      value() === valueOf(schema.password)
        ? null
        : { kind: 'passwordMismatch', message: 'Passwords must match' }
    );
    validateAsync(schema.password, {
      params: ({ value }) => {
        const password = value();
        return password.length < 6 ? undefined : password;
      },
      factory: (passwordSignal: Signal<string | undefined>) => {
        return resource({
          params: () => passwordSignal(),
          loader: async ({ params: password }) => {
            if (!password) return undefined;

            return this.stytch.passwordStrengthCheck(password);
          },
        });
      },
      onSuccess: (result) =>
        result.valid_password
          ? null
          : { kind: 'weakPassword', message: passwordFeedback(result) },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.error('Password validation check failed', err);
        return {
          kind: 'serverError',
          message:
            'Could not check that password meets requirements. Please try again.',
        };
      },
    });
  });
  private currentParams = signal<ParamMap>(null);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((params) => {
          this.currentParams.set(params);
          return params.get('token');
        })
      )
      .subscribe({
        next: (token) => {
          if (token) {
            this.passwordResetToken.set(token);
          }
        },
      });
  }

  togglePassword() {
    this.showPassword.update((show) => !show);
  }

  submit() {
    if (this.passwordResetForm().invalid()) {
      this.alertFlash.addDanger(
        this.passwordResetForm().errorSummary()[0]?.message
      );
      return;
    }
    this.submitting.set(true);
    this.stytch
      .updatePassword(this.passwordReset().password, this.passwordResetToken())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => this.postLogin.redirect(this.currentParams()),
        error: (err: { error_message?: string; message?: string }) => {
          this.alertFlash.addDanger(err?.error_message ?? err?.message);
        },
      });
  }
}
